/**
 * SEO UTILITIES
 * ─────────────────────────────────────────────────────────────
 * Automatically detects the environment by hostname:
 *
 *   www.devbrains.in          → PRODUCTION  — full SEO, index/follow
 *   devbrains.in              → PRODUCTION  — full SEO, index/follow
 *   qa.jsdevbrains.in         → QA          — noindex, nofollow, no JSON-LD
 *   localhost / 127.0.0.1     → LOCAL DEV   — noindex, nofollow
 *   anything else             → UNKNOWN     → noindex (safe default)
 *
 * Profile pages work like LinkedIn:
 *   - Title:       "Amar Krishnan - Senior Software Engineer at Ex-Google | DevBrains"
 *   - Description: "Amar Krishnan is a verified Senior Software Engineer with 8 years
 *                   experience. Book a 1:1 session on DevBrains."
 *   - OG image:    Dynamic avatar via DiceBear (300×300, PNG-compatible)
 *   - og:type:     "profile"  with profile:first_name / profile:last_name
 *   - JSON-LD:     Full Schema.org/Person with sameAs, knowsAbout, offers
 *   - Twitter:     summary_large_image card
 *   - Canonical:   https://www.devbrains.in/mentors/{slug}
 */

import { useEffect } from 'react';

/* ── Domain config ───────────────────────────────────────── */
const PROD_DOMAINS = ['www.devbrains.in', 'devbrains.in'];
const QA_DOMAINS   = ['qa.jsdevbrains.in'];

export const SITE_NAME = 'DevBrains';
export const PROD_URL  = 'https://www.devbrains.in';
export const TWITTER_HANDLE = '@DevBrains_in';

const DEFAULT_DESCRIPTION =
  'Connect with verified engineers from Google, Shopee, Grab and more for ' +
  '1:1 mentorship, code reviews, mock interviews, and project-based certifications.';
const DEFAULT_OG_IMAGE = `${PROD_URL}/og-image.png`;   // 1200×630 brand image

/* ── Environment detection ───────────────────────────────── */
function detectEnv() {
  if (typeof window === 'undefined') return 'production';
  const host = window.location.hostname.toLowerCase().replace(/^www\./, '');
  if (PROD_DOMAINS.map(d => d.replace(/^www\./, '')).includes(host)) return 'production';
  if (QA_DOMAINS.map(d => d.replace(/^www\./, '')).includes(host))   return 'qa';
  return 'dev';
}

export const ENV     = detectEnv();
export const IS_PROD = ENV === 'production';
export const IS_QA   = ENV === 'qa';

/* ── Avatar / profile image URL ─────────────────────────── */
/**
 * Returns the canonical image URL for a mentor profile.
 * Uses DiceBear Personas (SVG — works for OG image preview).
 * When a real photo upload system exists, swap this function only.
 *
 * LinkedIn uses 300×300 for profile cards.
 * Twitter uses 800×418 for summary_large_image.
 * We use the same DiceBear URL for both (bots render the SVG fine).
 */
export function getMentorOgImage(mentor) {
  if (mentor?.photoUrl) return mentor.photoUrl;   // real photo upload (future)
  // DiceBear Personas — deterministic per seed, stable URL
  const seed = mentor?.avatarSeed || mentor?.name || 'mentor';
  const bg   = mentor?.avatarBg   || '1DB954';
  return `https://api.dicebear.com/9.x/personas/svg?seed=${encodeURIComponent(seed)}&backgroundColor=${bg}`;
}

/* ── useSEO hook ─────────────────────────────────────────── */
/**
 * @param {object} opts
 *   title       {string}   Page-specific title (without site name)
 *   description {string}   Meta description
 *   image       {string}   Absolute OG image URL (default: brand OG image)
 *   type        {string}   og:type — 'website' | 'profile' | 'article'
 *   noIndex     {boolean}  Force noindex even on prod
 *   jsonLd      {object}   Schema.org structured data object
 *   profile     {object}   { firstName, lastName, username }  — for og:type="profile"
 *   canonical   {string}   Override canonical URL
 */
export function useSEO({
  title,
  description,
  image,
  type      = 'website',
  noIndex   = false,
  jsonLd    : jsonLdData,
  profile,            // { firstName, lastName, username }
  canonical,
}) {
  const fullTitle  = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const desc       = description || DEFAULT_DESCRIPTION;
  const img        = image || DEFAULT_OG_IMAGE;
  const currentUrl = canonical || (typeof window !== 'undefined' ? window.location.href : PROD_URL);
  const shouldIndex = IS_PROD && !noIndex;

  useEffect(() => {
    /* ── Always: title + robots ───────────────────────────── */
    document.title = IS_QA ? `[QA] ${fullTitle}` : fullTitle;
    setMeta('robots', shouldIndex ? 'index, follow' : 'noindex, nofollow');

    /* ── Non-prod: strip all OG/LD and bail ──────────────── */
    if (!IS_PROD) {
      document.querySelectorAll('meta[property^="og:"], meta[property^="profile:"], meta[name^="twitter:"]')
        .forEach(el => el.remove());
      const ld = document.querySelector('#json-ld-dynamic');
      if (ld) ld.remove();
      return;
    }

    /* ── Production: full SEO stack ──────────────────────── */
    // Standard meta
    setMeta('description', desc);

    // Canonical
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement('link'); canon.rel = 'canonical'; document.head.appendChild(canon); }
    canon.href = currentUrl;

    // Open Graph — core
    setMetaProp('og:title',       fullTitle);
    setMetaProp('og:description', desc);
    setMetaProp('og:url',         currentUrl);
    setMetaProp('og:type',        type);
    setMetaProp('og:site_name',   SITE_NAME);
    setMetaProp('og:image',       img);
    setMetaProp('og:image:width', '300');
    setMetaProp('og:image:height','300');
    setMetaProp('og:image:alt',   title || SITE_NAME);
    setMetaProp('og:locale',      'en_IN');

    // Open Graph — profile-specific (like LinkedIn)
    if (type === 'profile' && profile) {
      setMetaProp('profile:first_name', profile.firstName || '');
      setMetaProp('profile:last_name',  profile.lastName  || '');
      if (profile.username) setMetaProp('profile:username', profile.username);
    } else {
      // Remove profile meta if navigating away from a profile page
      ['profile:first_name','profile:last_name','profile:username']
        .forEach(p => { const el = document.querySelector(`meta[property="${p}"]`); if (el) el.remove(); });
    }

    // Twitter Card
    setMeta('twitter:card',        type === 'profile' ? 'summary' : 'summary_large_image');
    setMeta('twitter:site',        TWITTER_HANDLE);
    setMeta('twitter:title',       fullTitle);
    setMeta('twitter:description', desc);
    setMeta('twitter:image',       img);
    setMeta('twitter:image:alt',   title || SITE_NAME);

    // JSON-LD Structured Data
    if (jsonLdData) {
      let el = document.querySelector('#json-ld-dynamic');
      if (!el) { el = document.createElement('script'); el.type = 'application/ld+json'; el.id = 'json-ld-dynamic'; document.head.appendChild(el); }
      el.textContent = JSON.stringify(jsonLdData);
    }

    return () => {
      const el = document.querySelector('#json-ld-dynamic');
      if (el && jsonLdData) el.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullTitle, desc, img, currentUrl, type, shouldIndex]);
}

/* ── DOM helpers ─────────────────────────────────────────── */
function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.name = name; document.head.appendChild(el); }
  el.content = content;
}
function setMetaProp(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
  el.content = content;
}

/* ── JSON-LD builders ────────────────────────────────────── */
export const jsonLd = {

  /**
   * Organization — used on Home, Mentors list, etc.
   */
  organization: () => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: PROD_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${PROD_URL}/logo.png`,
      width: 200, height: 200,
    },
    sameAs: [
      'https://linkedin.com/company/jsdevbrains',
      'https://twitter.com/devbrains_in',
      'https://instagram.com/devbrains.in',
    ],
    description: DEFAULT_DESCRIPTION,
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'IN',
      addressLocality: 'Bengaluru',
    },
  }),

  /**
   * Person — LinkedIn-equivalent profile schema.
   * Used on every /mentors/:slug page.
   *
   * Includes:
   *   - name, jobTitle, worksFor
   *   - description (bio)
   *   - image (DiceBear or uploaded photo)
   *   - url (canonical DevBrains profile URL)
   *   - sameAs (LinkedIn, Twitter, GitHub if provided)
   *   - knowsAbout (skills array → schema topics)
   *   - hasOccupation (Occupation schema)
   *   - offers (MentorshipService)
   *   - aggregateRating
   *   - alumniOf / worksFor
   */
  person: (mentor) => {
    const profileUrl = `${PROD_URL}/mentors/${mentor.slug}`;
    const imageUrl   = getMentorOgImage(mentor);
    const sameAs     = [];
    if (mentor.linkedin) sameAs.push(mentor.linkedin);
    if (mentor.twitter)  sameAs.push(mentor.twitter);
    if (mentor.github)   sameAs.push(mentor.github);
    if (mentor.website)  sameAs.push(mentor.website);

    return {
      '@context': 'https://schema.org',
      '@type': 'Person',

      /* ── Core identity ── */
      name:       mentor.name,
      givenName:  mentor.name.split(' ')[0],
      familyName: mentor.name.split(' ').slice(1).join(' '),
      jobTitle:   mentor.role,
      description: mentor.bio,
      url:        profileUrl,
      image: {
        '@type':  'ImageObject',
        url:       imageUrl,
        width:     300,
        height:    300,
      },

      /* ── Where they work / worked ── */
      worksFor: {
        '@type': 'Organization',
        name:    mentor.company,
      },

      /* ── Location ── */
      address: {
        '@type':         'PostalAddress',
        addressLocality: (mentor.location || '').split(',')[0]?.trim(),
        addressCountry:  mentor.location?.includes('India') ? 'IN'
                       : mentor.location?.includes('Malaysia') ? 'MY'
                       : mentor.location?.includes('Singapore') ? 'SG' : '',
      },

      /* ── Social & web profiles (LinkedIn-style sameAs) ── */
      ...(sameAs.length > 0 ? { sameAs } : {}),

      /* ── Skills / expertise ── */
      knowsAbout: (mentor.skills || []).map(skill => ({
        '@type': 'Thing',
        name:    skill,
      })),

      /* ── Occupation (rich for Google Jobs-adjacent indexing) ── */
      hasOccupation: {
        '@type':               'Occupation',
        name:                  mentor.role,
        occupationalCategory:  mentor.specialization || 'Software Engineering',
        estimatedSalary:       undefined, // not public
        skills:                (mentor.skills || []).join(', '),
        experienceRequirements: `${mentor.experience || 0} years`,
      },

      /* ── Languages ── */
      knowsLanguage: (mentor.languages || ['English']).map(lang => ({
        '@type': 'Language',
        name:    lang,
      })),

      /* ── Mentorship offering (Service schema) ── */
      makesOffer: {
        '@type':       'Offer',
        name:          `1:1 Mentorship with ${mentor.name}`,
        description:   `Book a private mentorship session with ${mentor.name}, ${mentor.role}. Topics: ${(mentor.skills || []).slice(0,3).join(', ')}.`,
        url:           `${profileUrl}/book`,
        price:         mentor.priceINR || 0,
        priceCurrency: 'INR',
        seller: {
          '@type': 'Person',
          name:    mentor.name,
          url:     profileUrl,
        },
      },

      /* ── Aggregate rating (from reviews) ── */
      ...(mentor.rating ? {
        aggregateRating: {
          '@type':       'AggregateRating',
          ratingValue:   mentor.rating,
          reviewCount:   mentor.reviewCount || 1,
          bestRating:    5,
          worstRating:   1,
        },
      } : {}),

      /* ── Member of DevBrains ── */
      memberOf: {
        '@type': 'Organization',
        name:    SITE_NAME,
        url:     PROD_URL,
      },
    };
  },

  /**
   * BreadcrumbList — for profile pages
   *   Home > Find Mentors > Amar Krishnan
   */
  profileBreadcrumb: (mentor) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home',         item: PROD_URL },
      { '@type': 'ListItem', position: 2, name: 'Find Mentors', item: `${PROD_URL}/mentors` },
      { '@type': 'ListItem', position: 3, name: mentor.name,    item: `${PROD_URL}/mentors/${mentor.slug}` },
    ],
  }),

  /**
   * Course — used on /certifications
   */
  course: (cert) => ({
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: cert.name,
    description: cert.desc,
    provider: { '@type': 'Organization', name: SITE_NAME, url: PROD_URL },
    url: `${PROD_URL}/certifications/${cert.slug || ''}`,
    courseMode: 'online',
    educationalLevel: cert.level,
    image: `${PROD_URL}/og-image.png`,
    offers: {
      '@type': 'Offer',
      price: cert.priceINR || 0,
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock',
      url: `${PROD_URL}/certifications`,
    },
  }),

  /**
   * Event — used on /events
   */
  event: (ev) => ({
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: ev.title,
    startDate: ev.date,
    description: ev.description,
    organizer: {
      '@type': 'Person',
      name:    ev.mentor,
      url:     ev.mentorSlug ? `${PROD_URL}/mentors/${ev.mentorSlug}` : PROD_URL,
    },
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    eventStatus:         'https://schema.org/EventScheduled',
    url:                 `${PROD_URL}/events/${ev.id}`,
    offers: ev.priceINR === 0 ? {
      '@type': 'Offer', price: 0, priceCurrency: 'INR',
      availability: 'https://schema.org/InStock',
    } : {
      '@type': 'Offer', price: ev.priceINR, priceCurrency: 'INR',
      availability: ev.registeredCount >= ev.maxAttendees
        ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
    },
    ...(ev.rating ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: ev.rating, reviewCount: ev.totalRatings || 1,
        bestRating: 5, worstRating: 1,
      },
    } : {}),
  }),

  /**
   * FAQPage — for the Home page FAQ section
   */
  faqPage: (faqs) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  }),

  /**
   * Generic breadcrumb builder
   */
  breadcrumb: (items) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${PROD_URL}${item.path}`,
    })),
  }),

  /**
   * Combined graph — multiple @type objects in one script tag.
   * Use on profile pages: [ Person, BreadcrumbList ]
   */
  graph: (...nodes) => ({
    '@context': 'https://schema.org',
    '@graph': nodes,
  }),
};

/* ── Sitemap & robots ────────────────────────────────────── */
export const SITEMAP_ROUTES = [
  { path: '/',               changefreq: 'daily',   priority: 1.0 },
  { path: '/mentors',        changefreq: 'daily',   priority: 0.9 },
  { path: '/certifications', changefreq: 'weekly',  priority: 0.8 },
  { path: '/events',         changefreq: 'daily',   priority: 0.8 },
  { path: '/training',       changefreq: 'weekly',  priority: 0.8 },
  { path: '/news',           changefreq: 'weekly',  priority: 0.7 },
  { path: '/become-mentor',  changefreq: 'monthly', priority: 0.7 },
];

/**
 * Article JSON-LD — for /training/:slug pages
 */
export const articleJsonLd = (article) => ({
  '@context':    'https://schema.org',
  '@type':       'Article',
  headline:      article.title,
  description:   article.excerpt,
  datePublished: article.publishedAt,
  dateModified:  article.updatedAt || article.publishedAt,
  image:         `${PROD_URL}/og-training.jpg`,
  author: {
    '@type': 'Person',
    name:    article.authorName || 'DevBrains Team',
    url:     PROD_URL,
  },
  publisher: {
    '@type': 'Organization',
    name:    SITE_NAME,
    logo:    { '@type': 'ImageObject', url: `${PROD_URL}/logo192.png` },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id':   `${PROD_URL}/training/${article.slug}`,
  },
});

/**
 * Training breadcrumb builder
 */
export const trainingBreadcrumb = (article) => ({
  '@context': 'https://schema.org',
  '@type':    'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home',     item: PROD_URL },
    { '@type': 'ListItem', position: 2, name: 'Training', item: `${PROD_URL}/training` },
    { '@type': 'ListItem', position: 3, name: article.title, item: `${PROD_URL}/training/${article.slug}` },
  ],
});

export const ROBOTS_TXT = `User-agent: *
Allow: /
Disallow: /dashboard/
Disallow: /api/

Sitemap: ${PROD_URL}/sitemap.xml`;
